import {
  getAnonymousId,
  getSessionId,
  getSessionMarketingParams,
  getSessionReferrer,
  getLandingPage,
  getCurrentPageUrl,
  getAttributionConsent,
} from '@/lib/attribution/storage'
import { AuthAttributionFields } from '@/lib/attribution/types'
import { ATTRIBUTION_ENABLED } from '@/lib/config'
import { attributionDebug } from '@/lib/attribution/logger'

export function withAttributionData<T extends Record<string, unknown>>(
  payload: T,
): T & AuthAttributionFields {
  if (typeof window === 'undefined') {
    return payload
  }

  if (!ATTRIBUTION_ENABLED) {
    attributionDebug('Skipping attribution enrichment because attribution is disabled')
    return payload
  }

  const anonymousId = getAnonymousId()
  if (!anonymousId) {
    attributionDebug('anonymous_id not available; returning payload unchanged')
    return payload
  }

  const sessionId = getSessionId()
  const marketingParams = getSessionMarketingParams()
  const referrerUrl = getSessionReferrer()
  const landingPage = getLandingPage()
  const pageUrl = getCurrentPageUrl()
  const consent = getAttributionConsent()

  const fields: AuthAttributionFields = {
    anonymous_id: anonymousId,
    ...(sessionId && { session_id: sessionId }),
    ...(marketingParams.utm_source && { utm_source: marketingParams.utm_source }),
    ...(marketingParams.utm_medium && { utm_medium: marketingParams.utm_medium }),
    ...(marketingParams.utm_campaign && { utm_campaign: marketingParams.utm_campaign }),
    ...(marketingParams.utm_term && { utm_term: marketingParams.utm_term }),
    ...(marketingParams.utm_content && { utm_content: marketingParams.utm_content }),
    ...(marketingParams.gclid && { gclid: marketingParams.gclid }),
    ...(marketingParams.fbclid && { fbclid: marketingParams.fbclid }),
    ...(referrerUrl && { referrer_url: referrerUrl }),
    ...(landingPage && { landing_page: landingPage }),
    ...(pageUrl && { page_url: pageUrl }),
    ...(consent !== 'unknown' && { consent_given: consent === 'granted' }),
  }

  attributionDebug('Added attribution data to auth payload', {
    fieldKeys: Object.keys(fields),
    payloadKeys: Object.keys(payload),
  })

  return {
    ...payload,
    ...fields,
  }
}

function isAbsoluteUrl(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(value)
}

export function appendAidToRegisterUrl(url: string): string {
  if (typeof window === 'undefined') return url

  if (!ATTRIBUTION_ENABLED) {
    attributionDebug('Skipping aid append for register URL because attribution is disabled', {
      originalUrl: url,
    })
    return url
  }

  const anonymousId = getAnonymousId()
  if (!anonymousId) {
    attributionDebug('Skipping aid append for register URL because anonymous_id is missing', {
      originalUrl: url,
    })
    return url
  }

  try {
    const parsed = new URL(url, window.location.origin)
    parsed.searchParams.set('aid', anonymousId)

    if (isAbsoluteUrl(url)) {
      const resolved = parsed.toString()
      attributionDebug('Appended aid to absolute register URL', {
        originalUrl: url,
        resolvedUrl: resolved,
        anonymousId,
      })
      return resolved
    }

    const resolved = `${parsed.pathname}${parsed.search}${parsed.hash}`
    attributionDebug('Appended aid to relative register URL', {
      originalUrl: url,
      resolvedUrl: resolved,
      anonymousId,
    })
    return resolved
  } catch {
    attributionDebug('Failed to append aid to register URL; returning original URL', {
      originalUrl: url,
    })
    return url
  }
}
